import { Device } from '@twilio/voice-sdk';

let device = null;

export const initializeDevice = async (token) => {
  try {
    device = new Device(token, {
      logLevel: 1, // Debug level
      closeProtection: false,
      enableImprovedSignalingErrorPrecision: true,
      // Prevent SDK from auto-playing sounds to satisfy browser autoplay policy
      disableAudioContextSounds: true,
      // Suppress ringtone
      sounds: {
        incoming: false,
        outgoing: false,
        disconnect: false,
      },
    });

    // Setup event listeners (Voice SDK v2.x uses Device.EventName constants)
    device.on(Device.EventName.Registered, () => {
      console.log('Twilio Device registered and ready');
    });

    device.on(Device.EventName.Error, (error) => {
      console.error('Twilio Device error:', error);
    });

    device.on(Device.EventName.Incoming, (call) => {
      console.log('Incoming call received', call);
    });

    device.on(Device.EventName.Unregistered, () => {
      console.log('Twilio Device unregistered');
    });

    // Register device to enable making and receiving calls
    await device.register();

    console.log('Device initialized and registered successfully');
    return device;
  } catch (error) {
    console.error('Failed to initialize Twilio Device:', error);
    throw error;
  }
};

export const getDevice = () => {
  if (!device) {
    throw new Error('Device not initialized');
  }
  return device;
};

/**
 * Make an outgoing call with optional parameters.
 * @param {string} phoneNumber - Destination phone number (required)
 * @param {Object} options - Optional call parameters
 * @param {string} options.patientGuid - Patient GUID for lookup
 * @param {string} options.productLine - Product line (e.g., "CPAP", "CGM")
 * @param {boolean} options.autodialer - Auto-dialer flag
 * @param {Object} options.customParams - Additional custom parameters to merge
 */
export const makeCall = async (phoneNumber, options = {}) => {
  const device = getDevice();
  const { patientGuid, productLine, autodialer, customParams = {} } = options;

  const params = {
    To: phoneNumber,
  };

  // Only include optional parameters if they have values
  if (patientGuid) params.patientGuid = patientGuid;
  if (productLine) params.productLine = productLine;
  if (autodialer !== undefined) params.autodialer = autodialer;

  // Merge custom parameters
  Object.assign(params, customParams);

  try {
    const call = await device.connect({ params });
    console.log('Call initiated with params:', params);
    return call;
  } catch (error) {
    console.error('Failed to make call:', error);
    throw error;
  }
};

export const answerCall = (connection) => {
  try {
    connection.accept();
    console.log('Call answered');
  } catch (error) {
    console.error('Failed to answer call:', error);
    throw error;
  }
};

export const rejectCall = (connection) => {
  try {
    connection.reject();
    console.log('Call rejected');
  } catch (error) {
    console.error('Failed to reject call:', error);
    throw error;
  }
};

export const hangupAll = () => {
  try {
    const device = getDevice();
    device.disconnectAll();
    console.log('All calls disconnected');
  } catch (error) {
    console.error('Failed to hang up:', error);
    throw error;
  }
};

export const toggleMute = (connection) => {
  const isMuted = connection.isMuted();
  connection.mute(!isMuted);
  return !isMuted;
};

export const sendDigits = (connection, digits) => {
  if (/^[0-9*#]+$/.test(digits)) {
    connection.sendDigits(digits);
  }
};

export const cleanupDevice = () => {
  if (device) {
    device.destroy();
    device = null;
    console.log('Device cleaned up');
  }
};
